// Phase 11 — Single source of truth for keyboard shortcuts.
// Detects platform, exposes a normalized 2-key shortcut table, and provides
// a global window-level event dispatcher. No 3-key combos anywhere.

const SHORTCUTS = [
    { id: "command-palette",   action: "Open command palette",     mac: "⌘ K", win: "Ctrl K", key: "k" },
    { id: "new-analysis",      action: "New analysis",             mac: "⌘ N", win: "Ctrl N", key: "n" },
    { id: "toggle-voice",      action: "Toggle voice input",       mac: "⌘ M", win: "Ctrl M", key: "m" },
    { id: "copy-permalink",    action: "Copy permalink",           mac: "⌘ .", win: "Ctrl .", key: "." },
    { id: "open-how-it-works", action: "Open How it works",        mac: "⌘ /", win: "Ctrl /", key: "/" },
    { id: "close-panel",       action: "Close any open panel",     mac: "Esc", win: "Esc",    key: "escape" },
];

export function isMacLike() {
    if (typeof navigator === "undefined") return false;
    const ua = (navigator.userAgent || "").toLowerCase();
    const platform = (navigator.userAgentData?.platform || navigator.platform || "").toLowerCase();
    if (platform.includes("mac") || platform.includes("iphone") || platform.includes("ipad")) return true;
    if (ua.includes("mac os") || ua.includes("iphone") || ua.includes("ipad")) return true;
    return false;
}

export function shortcutLabel(id) {
    const s = SHORTCUTS.find((x) => x.id === id);
    if (!s) return "";
    return isMacLike() ? s.mac : s.win;
}

export function getShortcuts() {
    return SHORTCUTS.map((s) => ({ ...s, label: isMacLike() ? s.mac : s.win }));
}

/** Returns true if the event matches the given shortcut id. */
function matches(e, id) {
    const s = SHORTCUTS.find((x) => x.id === id);
    if (!s) return false;
    const cmdLike = isMacLike() ? e.metaKey : e.ctrlKey;
    if (s.key === "escape") return e.key === "Escape" && !cmdLike && !e.shiftKey && !e.altKey;
    if (e.shiftKey || e.altKey) return false;
    return cmdLike && e.key.toLowerCase() === s.key;
}

/**
 * Install global shortcut listener.
 * Dispatches `window.dispatchEvent(new CustomEvent('scope-shortcut:<id>'))`.
 *
 * Returns an unsubscribe function.
 */
export function installShortcuts(opts = {}) {
    if (typeof window === "undefined") return () => {};
    const handler = (e) => {
        for (const s of SHORTCUTS) {
            if (matches(e, s.id)) {
                if (opts.preventDefaultFor !== false) {
                    e.preventDefault();
                }
                window.dispatchEvent(new CustomEvent(`scope-shortcut:${s.id}`));
                if (s.id === "toggle-voice") {
                    window.dispatchEvent(new CustomEvent("scope-toggle-voice"));
                }
                return;
            }
        }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
}

/** React hook — subscribe to a specific shortcut id. */
import { useEffect } from "react";
export function useShortcut(id, callback) {
    useEffect(() => {
        const evt = `scope-shortcut:${id}`;
        const h = () => callback();
        window.addEventListener(evt, h);
        return () => window.removeEventListener(evt, h);
    }, [id, callback]);
}

export const SHORTCUT_TABLE = SHORTCUTS;
